import { Formik, FormikProps } from 'formik';
import { cloneDeep } from 'lodash';
import {
  FunctionComponent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import yup from 'yup';
import { useRegisterBackButtonCallback } from '../../../../../../components/header/back-button';
import { useIsMounted } from '../../../../../../util/hooks';
import { useFlowServices } from '../../../hook/flow-service.hooks';
import {
  FlowItem,
  FlowItemForm,
  FlowItemParameterRef,
} from '../../../model/flow.types';
import { useBackButtonConfirmationAlert } from '../../alert/back-confirmation-alert';
import {
  FlowItemFormContext,
  FlowItemFormContextValue,
} from './flow-item-form.context';

export interface FlowItemFormComponentProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: FlowItem & { [key: string]: any };
  validationSchema?: yup.AnySchema;
  onSubmit: (item: FlowItem) => Promise<void>;
  children: FunctionComponent<
    FormikProps<FlowItemForm> & FlowItemFormContextValue
  >;
  hasFlowChanges?: boolean;
}

const PARAMETER_LIST_FORM_PREFIX = 'parameters';

const FlowItemFormComponent = (
  props: FlowItemFormComponentProps
): ReactElement => {
  const { flowItemService, system, flow } = useFlowServices();
  const { item, onSubmit, hasFlowChanges } = props;

  const isMounted = useIsMounted();
  const [validationSchema, setValidationSchema] = useState<
    yup.AnySchema | undefined
  >();

  const [isUpdated, setIsUpdated] = useState<boolean>(false);

  useEffect(() => {
    if (hasFlowChanges !== undefined) {
      setIsUpdated(hasFlowChanges);
    }
  }, [hasFlowChanges]);

  const { alert, openAlert } = useBackButtonConfirmationAlert();

  const handleBackButton = useCallback((): boolean => {
    if (isUpdated) {
      openAlert();
      return false;
    }
    return true;
  }, [openAlert, isUpdated]);

  useRegisterBackButtonCallback(handleBackButton);

  useEffect(() => {
    const schema =
      props.validationSchema ??
      flowItemService.getYupSchemaForItem(item.ref, system, {
        flow,
        form: flowItemService.convertFlowItemToForm(item, system),
      });
    setValidationSchema(schema);
  }, [item.ref, props.validationSchema, system, flowItemService, item, flow]);

  const onSubmitInternal = async (form: FlowItemForm) => {
    const itemToSave = flowItemService.convertFormToFlowItem(form, system);
    onSubmit(cloneDeep(itemToSave));
  };

  const form: FlowItemForm = useMemo(() => {
    return flowItemService.convertFlowItemToForm(item, system);
  }, [item, system, flowItemService]);

  return (
    <>
      <Formik
        onSubmit={onSubmitInternal}
        initialValues={form}
        validationSchema={validationSchema}
        validateOnMount={true}
        enableReinitialize={true}
      >
        {(formikProps) => {
          const { dirty, setFieldValue, validateForm } = formikProps;

          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            setIsUpdated(dirty);
          }, [dirty]);

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const contextValue = useMemo((): FlowItemFormContextValue => {
            return {
              setValidationSchema: (schema: yup.AnySchema): void => {
                // small delay before setting state to prevent the child component
                // from setting the validation schema before the parents useEffect
                // has been called to set it
                requestAnimationFrame(() => {
                  if (isMounted()) {
                    setValidationSchema(schema);

                    // we cannot immediately call validateForm here as it
                    // might not have propagated to the formik component yet
                    setTimeout(() => {
                      validateForm();
                    }, 1);
                  }
                });
              },
              setParameterValue: (
                ref: FlowItemParameterRef,
                value: unknown,
                shouldValidate = true
              ): void => {
                setFieldValue(
                  `${PARAMETER_LIST_FORM_PREFIX}.${ref}.value`,
                  value,
                  shouldValidate
                );
              },
            };
          }, [validateForm, setFieldValue]);

          return (
            <FlowItemFormContext.Provider value={contextValue}>
              {props.children({ ...contextValue, ...formikProps })}
            </FlowItemFormContext.Provider>
          );
        }}
      </Formik>
      {alert}
    </>
  );
};

export { FlowItemFormComponent, PARAMETER_LIST_FORM_PREFIX };
